import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.clientOptimistic) {
      const didSelectExistingSlide = this.#selectExistingVariantSlide(event.detail.resource);
      if (!didSelectExistingSlide) {
        this.#swapActiveSlideImage(event.detail.resource);
      }
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Selects an existing slide when the variant media is already present in the current gallery.
   * @param {any} variant
   * @returns {boolean}
   */
  #selectExistingVariantSlide(variant) {
    const mediaId = variant?.featured_media?.id;
    if (!mediaId || !this.refs.slideshow?.refs?.slides) return false;

    const requestedSlide = this.refs.slideshow.refs.slides.find((slide) => slide.getAttribute('slide-id') === String(mediaId));
    if (!requestedSlide) return false;

    this.slideshow?.select({ id: String(mediaId) }, undefined, { animate: false });
    return true;
  }

  /**
   * Replaces the currently active image when the variant points to media that is not rendered in the gallery yet.
   * @param {any} variant
   */
  #swapActiveSlideImage(variant) {
    const activeSlide =
      this.refs.slideshow?.refs?.slides?.[this.slideshow?.current ?? 0] ||
      this.querySelector('slideshow-slide[aria-hidden="false"]');
    if (!(activeSlide instanceof HTMLElement)) return;

    const image = /** @type {HTMLImageElement | null} */ (activeSlide.querySelector('img.product-media__image'));
    const productMedia = /** @type {HTMLElement | null} */ (activeSlide.querySelector('.product-media'));
    const fallbackImage = variant?.featured_image || variant?.featured_media?.preview_image;
    const fallbackSrc = fallbackImage?.src;
    if (!image || !fallbackSrc) return;

    const resolvedSrc = fallbackSrc.startsWith('//') ? `https:${fallbackSrc}` : fallbackSrc;

    image.src = resolvedSrc;
    image.srcset = '';
    image.removeAttribute('sizes');
    image.setAttribute('fetchpriority', 'high');
    image.setAttribute('loading', 'eager');

    if (fallbackImage.alt != null) {
      image.alt = fallbackImage.alt;
    }

    if (productMedia && variant?.featured_media?.id) {
      productMedia.dataset.mediaId = String(variant.featured_media.id);
      activeSlide.setAttribute('slide-id', String(variant.featured_media.id));
    }
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
